import { jsx } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { getSelectedOperationName } from "@graphiql/toolkit";
import { getOperationFacts, getContextAtPosition } from "graphql-language-service";
import { useRef, useEffect } from "react";
import { useGraphiQLActions, useGraphiQL } from "./provider.js";
import { onEditorContainerKeyDown, getOrCreateModel, createEditor } from "../utility/create-editor.js";
import { clsx } from "clsx";
import { URI_NAME, MONACO_GRAPHQL_DIAGNOSTIC_SETTINGS, STORAGE_KEY, KEY_BINDINGS } from "../constants.js";
import { toGraphQLPosition } from "monaco-graphql/esm/utils.js";
import { useMonaco } from "../stores/monaco.js";
import { pick } from "../utility/pick.js";
import { debounce } from "../utility/debounce.js";
import { cleanupDisposables } from "../utility/cleanup-disposables.js";
import { URI } from "monaco-editor/esm/vs/base/common/uri.js";
import { Range } from "monaco-editor/esm/vs/editor/common/core/range.js";
const CURSOR_CHANGE_EXPLICIT = 3;
function getOperationNameAtCursor(editor, operations) {
  var _a;
  const position = editor.getPosition();
  if (!position) {
    return void 0;
  }
  const cursorIndex = editor.getModel().getOffsetAt(position);
  let operationName;
  for (const operation of operations) {
    if (operation.loc && operation.loc.start <= cursorIndex && operation.loc.end >= cursorIndex) {
      operationName = (_a = operation.name) == null ? void 0 : _a.value;
    }
  }
  return operationName;
}
const OperationEditor = (t0) => {
  const $ = c(56);
  const {
    onClickReference,
    onEdit,
    ...props
  } = t0;
  const {
    setOperationName,
    setEditor,
    updateActiveTabValues,
    setVisiblePlugin,
    setSchemaReference,
    run,
    setOperationFacts,
    copyQuery,
    prettifyEditors,
    mergeQuery,
    saveQuery
  } = useGraphiQLActions();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = pick("initialQuery", "schema", "customScalarSchemas", "referencePlugin", "operations", "operationName", "overrideOperationName", "externalFragments", "uriInstanceId", "storage");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    initialQuery,
    schema,
    customScalarSchemas,
    referencePlugin,
    operations,
    operationName,
    overrideOperationName,
    externalFragments,
    uriInstanceId,
    storage
  } = useGraphiQL(t1);
  const ref = useRef(null);
  const onClickReferenceRef = useRef(null);
  let t2;
  let t3;
  if ($[1] !== onClickReference) {
    t2 = () => {
      onClickReferenceRef.current = onClickReference;
    };
    t3 = [onClickReference];
    $[1] = onClickReference;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  const operationNameRef = useRef(operationName);
  const operationsRef = useRef(operations);
  const schemaRef = useRef(schema);
  let t4;
  let t5;
  if ($[4] !== operationName || $[5] !== operations || $[6] !== schema) {
    t4 = () => {
      operationNameRef.current = operationName;
      operationsRef.current = operations;
      schemaRef.current = schema;
    };
    t5 = [operationName, operations, schema];
    $[4] = operationName;
    $[5] = operations;
    $[6] = schema;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== setOperationFacts) {
    t6 = function getAndUpdateOperationFacts2(editorInstance) {
      const currentSchema = schemaRef.current;
      const currentOperations = operationsRef.current;
      const currentOperationName = operationNameRef.current;
      const operationFacts = getOperationFacts(currentSchema, editorInstance.getValue());
      const newOperationName = getSelectedOperationName(currentOperations, currentOperationName, operationFacts == null ? void 0 : operationFacts.operations);
      setOperationFacts({
        documentAST: operationFacts == null ? void 0 : operationFacts.documentAST,
        operationName: newOperationName,
        operations: operationFacts == null ? void 0 : operationFacts.operations
      });
      return operationFacts ? {
        ...operationFacts,
        operationName: newOperationName
      } : null;
    };
    $[9] = setOperationFacts;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const getAndUpdateOperationFacts = t6;
  const runAtCursorRef = useRef(null);
  let t7;
  let t8;
  if ($[11] !== operationName || $[12] !== operations || $[13] !== run || $[14] !== setOperationName) {
    t7 = () => {
      runAtCursorRef.current = (editor) => {
        if (!operations) {
          run();
          return;
        }
        const newOperationName_0 = getOperationNameAtCursor(editor, operations);
        if (newOperationName_0 && newOperationName_0 !== operationName) {
          setOperationName(newOperationName_0);
        }
        run();
      };
    };
    t8 = [operationName, operations, run, setOperationName];
    $[11] = operationName;
    $[12] = operations;
    $[13] = run;
    $[14] = setOperationName;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  const syncOperationNameToCursorRef = useRef(null);
  let t10;
  let t9;
  if ($[17] !== operationName || $[18] !== operations || $[19] !== overrideOperationName || $[20] !== setOperationName) {
    t9 = () => {
      syncOperationNameToCursorRef.current = (editor_0) => {
        if (!operations || typeof overrideOperationName === "string") {
          return;
        }
        const newOperationName_1 = getOperationNameAtCursor(editor_0, operations);
        if (newOperationName_1 && newOperationName_1 !== operationName) {
          setOperationName(newOperationName_1);
        }
      };
    };
    t10 = [operationName, operations, overrideOperationName, setOperationName];
    $[17] = operationName;
    $[18] = operations;
    $[19] = overrideOperationName;
    $[20] = setOperationName;
    $[21] = t10;
    $[22] = t9;
  } else {
    t10 = $[21];
    t9 = $[22];
  }
  useEffect(t9, t10);
  const {
    monacoGraphQL,
    monaco
  } = useMonaco();
  let t11;
  if ($[23] !== copyQuery || $[24] !== getAndUpdateOperationFacts || $[25] !== initialQuery || $[26] !== mergeQuery || $[27] !== monaco || $[28] !== monacoGraphQL || $[29] !== onEdit || $[30] !== prettifyEditors || $[31] !== saveQuery || $[32] !== setEditor || $[33] !== setOperationName || $[34] !== storage || $[35] !== updateActiveTabValues || $[36] !== uriInstanceId) {
    t11 = () => {
      if (!monaco || !monacoGraphQL) {
        return;
      }
      const operationUri = URI.file(`${uriInstanceId}${URI_NAME.operation}`);
      const variablesUri = URI.file(`${uriInstanceId}${URI_NAME.variables}`);
      const {
        validateVariablesJSON
      } = MONACO_GRAPHQL_DIAGNOSTIC_SETTINGS;
      validateVariablesJSON[operationUri.toString()] = [variablesUri.toString()];
      monacoGraphQL.setDiagnosticSettings(MONACO_GRAPHQL_DIAGNOSTIC_SETTINGS);
      const model = getOrCreateModel({
        uri: operationUri.path.replace("/", ""),
        value: initialQuery
      });
      const editor_1 = createEditor(ref, {
        model
      });
      setEditor({
        queryEditor: editor_1
      });
      const handleChange = debounce(100, () => {
        const query = editor_1.getValue();
        storage.set(STORAGE_KEY.query, query);
        const operationFacts_0 = getAndUpdateOperationFacts(editor_1);
        onEdit == null ? void 0 : onEdit(query, operationFacts_0 == null ? void 0 : operationFacts_0.documentAST);
        if ((operationFacts_0 == null ? void 0 : operationFacts_0.operationName) && operationNameRef.current !== operationFacts_0.operationName) {
          setOperationName(operationFacts_0.operationName);
        }
        updateActiveTabValues({
          query,
          operationName: (operationFacts_0 == null ? void 0 : operationFacts_0.operationName) ?? null
        });
      });
      getAndUpdateOperationFacts(editor_1);
      const syncCursorOperationName = debounce(100, () => {
        syncOperationNameToCursorRef.current(editor_1);
      });
      const disposables = [model.onDidChangeContent(handleChange), editor_1.onDidChangeCursorPosition((e) => {
        if (e.reason === CURSOR_CHANGE_EXPLICIT) {
          syncCursorOperationName();
        }
      }), {
        dispose: () => handleChange.cancel()
      }, {
        dispose: () => syncCursorOperationName.cancel()
      }, editor_1.addAction({
        ...KEY_BINDINGS.runQuery,
        run: (...t122) => {
          const args = t122;
          return runAtCursorRef.current(...args);
        }
      }), editor_1.addAction({
        ...KEY_BINDINGS.copyQuery,
        run: copyQuery
      }), editor_1.addAction({
        ...KEY_BINDINGS.prettify,
        run: prettifyEditors
      }), editor_1.addAction({
        ...KEY_BINDINGS.mergeFragments,
        run: mergeQuery
      }), editor_1.addAction({
        ...KEY_BINDINGS.saveQuery,
        run: saveQuery
      }), editor_1, model];
      return cleanupDisposables(disposables);
    };
    $[23] = copyQuery;
    $[24] = getAndUpdateOperationFacts;
    $[25] = initialQuery;
    $[26] = mergeQuery;
    $[27] = monaco;
    $[28] = monacoGraphQL;
    $[29] = onEdit;
    $[30] = prettifyEditors;
    $[31] = saveQuery;
    $[32] = setEditor;
    $[33] = setOperationName;
    $[34] = storage;
    $[35] = updateActiveTabValues;
    $[36] = uriInstanceId;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== monaco || $[39] !== monacoGraphQL) {
    t12 = [monaco, monacoGraphQL];
    $[38] = monaco;
    $[39] = monacoGraphQL;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[41] !== customScalarSchemas || $[42] !== externalFragments || $[43] !== monaco || $[44] !== monacoGraphQL || $[45] !== referencePlugin || $[46] !== schema || $[47] !== setSchemaReference || $[48] !== setVisiblePlugin || $[49] !== uriInstanceId) {
    t13 = () => {
      if (!schema || !monaco || !monacoGraphQL) {
        return;
      }
      monacoGraphQL.setSchemaConfig([{
        uri: `${uriInstanceId}${URI_NAME.schema}`,
        schema,
        customScalarSchemas
      }]);
      monacoGraphQL.setExternalFragmentDefinitions([...externalFragments.values()]);
      if (!referencePlugin) {
        return;
      }
      let currentSchemaReference = null;
      const disposables_0 = [monaco.languages.registerDefinitionProvider("graphql", {
        provideDefinition(model_0, position, _token) {
          const graphQLPosition = toGraphQLPosition(position);
          const context = getContextAtPosition(model_0.getValue(), graphQLPosition, schema);
          if (!context) {
            return;
          }
          const {
            typeInfo,
            token
          } = context;
          const {
            kind,
            step
          } = token.state;
          if (kind === "Field" && step === 0 && typeInfo.fieldDef || kind === "AliasedField" && step === 2 && typeInfo.fieldDef || kind === "ObjectField" && step === 0 && typeInfo.fieldDef || kind === "Directive" && step === 1 && typeInfo.directiveDef || kind === "Variable" && typeInfo.type || kind === "Argument" && step === 0 && typeInfo.argDef || kind === "EnumValue" && typeInfo.enumValue && "description" in typeInfo.enumValue || kind === "NamedType" && typeInfo.type && "description" in typeInfo.type) {
            currentSchemaReference = {
              kind,
              typeInfo
            };
            const {
              lineNumber,
              column
            } = position;
            const range = new Range(lineNumber, column, lineNumber, column);
            return [{
              uri: model_0.uri,
              range
            }];
          }
          currentSchemaReference = null;
        }
      }), monaco.languages.registerReferenceProvider("graphql", {
        provideReferences(model_1, t152, _context, _token_0) {
          var _a;
          const {
            lineNumber: lineNumber_0,
            column: column_0
          } = t152;
          if (!currentSchemaReference) {
            return;
          }
          setVisiblePlugin(referencePlugin);
          setSchemaReference(currentSchemaReference);
          (_a = onClickReferenceRef.current) == null ? void 0 : _a.call(onClickReferenceRef, currentSchemaReference);
          const range_0 = new Range(lineNumber_0, column_0, lineNumber_0, column_0);
          return [{
            uri: model_1.uri,
            range: range_0
          }];
        }
      })];
      return cleanupDisposables(disposables_0);
    };
    t14 = [schema, customScalarSchemas, referencePlugin, setSchemaReference, setVisiblePlugin, externalFragments, uriInstanceId, monacoGraphQL, monaco];
    $[41] = customScalarSchemas;
    $[42] = externalFragments;
    $[43] = monaco;
    $[44] = monacoGraphQL;
    $[45] = referencePlugin;
    $[46] = schema;
    $[47] = setSchemaReference;
    $[48] = setVisiblePlugin;
    $[49] = uriInstanceId;
    $[50] = t13;
    $[51] = t14;
  } else {
    t13 = $[50];
    t14 = $[51];
  }
  useEffect(t13, t14);
  const t15 = 0;
  const t16 = onEditorContainerKeyDown;
  const t17 = clsx("graphiql-editor", props.className);
  let t18;
  if ($[52] !== props || $[53] !== t16 || $[54] !== t17) {
    t18 = /* @__PURE__ */ jsx("div", { ref, tabIndex: t15, onKeyDown: t16, ...props, className: t17 });
    $[52] = props;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  return t18;
};
export {
  OperationEditor,
  getOperationNameAtCursor
};
//# sourceMappingURL=operation-editor.js.map
